import { Grid, Typography } from "@mui/material";
import FilterBuilderAddFilterSelect from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterBuilderAddFilterSelect";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxToggleButton from "components/UI/AcxToggleButton";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo } from "react";
import {
    createGroupByDraft,
    readGroupByDraft,
    validateGroupByDraft,
    writeGroupByDraft,
    type GroupByDraft,
} from "../../../Grouping/groupByDraft";
import {
    getDefaultSwapGroupingAndX,
    SignalsGroupByOptionLabels,
    type SignalsParsedVizOptions,
} from "../../../SignalsModels";
import ChartCardStore from "../../ChartCard/ChartCardStore";
import CategoricalGroupByEditor from "./CategoricalGroupByEditor";
import DateGroupByEditor from "./DateGroupByEditor";
import { configureInputLabelSx } from "./groupByOptionConfig";
import NumericRangeGroupByEditor from "./NumericRangeGroupByEditor";
import {
    useGroupByMenuOptions,
    type GroupByMenuOption,
} from "./useGroupByMenuOptions";

type Props = {
    chartStore: ChartCardStore;
    /** The drawer's working copy of the chart's vizOptions. Grouping is one part of it. */
    localVizOptions: SignalsParsedVizOptions;
    setLocalVizOptions: React.Dispatch<
        React.SetStateAction<SignalsParsedVizOptions>
    >;
    /** Explains why grouping is unavailable (currently: compare-previous is on). */
    disabledReason?: string;
    setDisableSave: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * Coordinates group-by configuration: pick a dimension, hand it to the editor for that dimension's
 * kind, and choose the chart's orientation. Everything kind-specific lives in the editors;
 * everything shared lives here.
 *
 * ## The draft
 *
 * This section has no state of its own. The drawer owns `localVizOptions`, and a *draft* is simply
 * that object's grouping read back as a `GroupByConfiguration`:
 *
 *     localVizOptions --readGroupByDraft--> draft --(user edits)--> writeGroupByDraft --> localVizOptions
 *
 * So `draft` is recomputed from `localVizOptions` on every render, and every edit round-trips
 * through the drawer's state rather than being held here. That keeps one source of truth (Cancel
 * discards `localVizOptions` and the draft goes with it) and keeps `writeGroupByDraft` as the only
 * place that decides how grouping is written -- canonical `groupBy` plus the legacy flat fields.
 *
 * "Draft" rather than "configuration" because it is allowed to be incomplete: an option with no
 * values chosen yet is a normal state to be in halfway through configuring a chart, and the
 * strict reader (`normalizeGroupByConfiguration`, used at request time) would reject it.
 * `validateGroupByDraft` decides when a draft is complete enough to save.
 */
export const GroupByConfigurationSection: React.FC<Props> = observer(
    ({
        chartStore,
        localVizOptions,
        setLocalVizOptions,
        disabledReason,
        setDisableSave,
    }) => {
        const { groupBySelectGroups, markGroupByLabel } =
            useGroupByMenuOptions(chartStore);

        // What the drawer currently holds, as grouping. Undefined means the chart is not grouped.
        const storedDraft = useMemo(
            () => readGroupByDraft(localVizOptions),
            [localVizOptions],
        );

        // Model Results grouping needs a classifier. Default it from the chart's current selection
        // so grouping setup starts from the context the user is already looking at.
        const fallbackLlmClassifierId = chartStore.selectedIds.llmClassifier as
            | string
            | undefined;

        // The draft as edited: what is stored, plus defaults we fill in for the user. Filling them
        // in here rather than in an effect means the editors and validation never see the
        // undefaulted state, so opening the drawer cannot flash a "select a model" error.
        const draft = useMemo(() => {
            if (
                storedDraft?.kind !== "categorical" ||
                storedDraft.option !== "ModelResults" ||
                storedDraft.llmClassifierId ||
                !fallbackLlmClassifierId
            ) {
                return storedDraft;
            }

            return {
                ...storedDraft,
                llmClassifierId: fallbackLlmClassifierId,
            };
        }, [fallbackLlmClassifierId, storedDraft]);

        // Every edit takes this path: the new draft replaces the grouping in the drawer's
        // vizOptions, which re-derives `draft` on the next render.
        const handleDraftChange = useCallback(
            (next: GroupByDraft | undefined) => {
                setLocalVizOptions((previous) =>
                    writeGroupByDraft(previous, next),
                );
            },
            [setLocalVizOptions],
        );

        useEffect(() => {
            // A defaulted draft is only shown until it is written back, otherwise Save would
            // persist the stored draft rather than what the drawer is displaying. The identity
            // check holds because `draft` is `storedDraft` itself whenever nothing was defaulted.
            if (draft !== storedDraft) {
                handleDraftChange(draft);
            }
        }, [draft, handleDraftChange, storedDraft]);

        // Grouping is the only part of the drawer that can block Save, so the section owns the
        // flag outright. A disabled section has no draft to validate and never blocks.
        //
        // The catalog entry carries what a range dimension's own filter can express, so the drawer
        // rejects a boundary past it while the user is still typing rather than on save.
        const draftCatalogEntry = draft?.option
            ? chartStore.groupingStore.catalogEntryFor(draft.option)
            : undefined;
        const validation = useMemo(
            () =>
                validateGroupByDraft(
                    disabledReason ? undefined : draft,
                    draftCatalogEntry,
                ),
            [disabledReason, draft, draftCatalogEntry],
        );

        useEffect(() => {
            // Save stays blocked while the grouping is incomplete so we never persist a partial
            // configuration the backend cannot resolve.
            setDisableSave(!validation.canSave);
        }, [setDisableSave, validation.canSave]);

        const isDonutChart = chartStore.chartType === "Donut";
        const selectedGroupByOption = draft?.option;
        // Extended Metadata is one option in the catalog but many entries in the menu, one per
        // groupable field, so the menu is keyed by field rather than by option.
        const selectedGroupBySelectValue =
            draft?.kind === "categorical" && draft.extendedMetadataKey
                ? `extendedMetadata.${draft.extendedMetadataKey}`
                : selectedGroupByOption;

        // The menu entry backing the current draft. It carries the labels the editors show, which
        // come from the filter picker and so can differ from the option's own name.
        const selectedGroupByMenuOption = useMemo(() => {
            const options = groupBySelectGroups.flatMap(
                (group) => group.options,
            );

            return (
                options.find(
                    (option) =>
                        option.selectValue === selectedGroupBySelectValue,
                ) ??
                (selectedGroupByOption === "ExtendedMetadata"
                    ? undefined
                    : options.find(
                          (option) => option.value === selectedGroupByOption,
                      ))
            );
        }, [
            groupBySelectGroups,
            selectedGroupByOption,
            selectedGroupBySelectValue,
        ]);

        // Used when the draft's option has no menu entry to take labels from -- an Extended
        // Metadata field the viewer can no longer see, for example.
        const fallbackOptionLabel = selectedGroupByOption
            ? markGroupByLabel(
                  selectedGroupByOption,
                  SignalsGroupByOptionLabels[selectedGroupByOption],
              )
            : undefined;
        const selectedGroupByValueLabel =
            selectedGroupByMenuOption?.valueLabel ??
            fallbackOptionLabel ??
            "Values";
        const selectedGroupByItemName =
            selectedGroupByMenuOption?.itemName ?? fallbackOptionLabel ?? "Value";

        // A grouped chart plots two dimensions, and this toggle chooses which one is the x-axis.
        // Temporal charts are always dated on x, and donuts have no axis to swap, so neither gets
        // a choice -- their orientation is forced below and on save.
        const showOrientChartByToggle =
            !!draft &&
            !chartStore.config.renderOptions.isTemporal &&
            !isDonutChart;
        const orientChartByValue =
            localVizOptions.swapGroupingAndX ??
            getDefaultSwapGroupingAndX({
                groupByOption: selectedGroupByOption,
                isTemporal: chartStore.config.renderOptions.isTemporal,
                endpoint: chartStore.dataEndpoint,
            });

        const handleGroupByOptionChange = (option?: GroupByMenuOption) => {
            // Clearing the menu ungroups the chart, which also retires the orientation it chose.
            if (!option) {
                setLocalVizOptions((previous) => ({
                    ...writeGroupByDraft(previous, undefined),
                    swapGroupingAndX: undefined,
                }));
                return;
            }

            // Switching dimensions starts a fresh draft: the previous dimension's values do not
            // carry over, which keeps mixed-mode payloads from being saved. The model stays,
            // since re-picking Model Results should not make the user choose it again.
            const nextDraft = createGroupByDraft(option.value, {
                extendedMetadataKey: option.extendedMetadataKey,
                llmClassifierId:
                    draft?.kind === "categorical"
                        ? draft.llmClassifierId
                        : undefined,
                // A range dimension opens on the boundaries the backend publishes for it, so the
                // user starts from a working set of groups rather than an empty builder. Only the
                // catalog knows them; the editor is told nothing about defaults.
                defaultCutPoints: chartStore.groupingStore.catalogEntryFor(
                    option.value,
                )?.defaultCutPoints,
            });

            // A new dimension gets the orientation that suits it rather than inheriting the one
            // the user picked for the previous dimension. Donuts have no x-axis, so their single
            // ring always plots the grouping.
            setLocalVizOptions((previous) => ({
                ...writeGroupByDraft(previous, nextDraft),
                swapGroupingAndX: isDonutChart
                    ? true
                    : getDefaultSwapGroupingAndX({
                          groupByOption: nextDraft.option,
                          isTemporal: chartStore.config.renderOptions.isTemporal,
                          endpoint: chartStore.dataEndpoint,
                      }),
            }));
        };

        /**
         * One editor per kind. Exhaustive rather than a chain of conditionals: a kind added without
         * an editor is then a compile error, instead of rendering whichever editor happened to be
         * last and handing it a draft it cannot describe.
         */
        const renderEditor = (activeDraft: GroupByDraft) => {
            switch (activeDraft.kind) {
                case "categorical":
                    return (
                        <CategoricalGroupByEditor
                            chartStore={chartStore}
                            draft={activeDraft}
                            onDraftChange={handleDraftChange}
                            valueLabel={selectedGroupByValueLabel}
                            itemName={selectedGroupByItemName}
                            validationMessage={validation.message}
                        />
                    );
                case "date":
                    return (
                        <DateGroupByEditor
                            chartStore={chartStore}
                            draft={activeDraft}
                            onDraftChange={handleDraftChange}
                            validationMessage={validation.message}
                        />
                    );
                case "numericRange":
                    return (
                        <NumericRangeGroupByEditor
                            chartStore={chartStore}
                            draft={activeDraft}
                            onDraftChange={handleDraftChange}
                            validationMessage={validation.message}
                        />
                    );
                default: {
                    const unhandledKind: never = activeDraft;
                    void unhandledKind;
                    return null;
                }
            }
        };

        return (
            <Grid container direction="column" rowGap={2}>
                <Grid item xs={12}>
                    <Grid container direction="column" rowGap={0.75}>
                        <Grid item>
                            <AcxInputLabel
                                htmlFor={`${chartStore.id}-group-by-menu`}
                                labelText="Group By"
                                sx={configureInputLabelSx}
                            />
                        </Grid>
                        <Grid item>
                            <FilterBuilderAddFilterSelect<GroupByMenuOption>
                                id={`${chartStore.id}-group-by-menu`}
                                options={groupBySelectGroups}
                                onChange={handleGroupByOptionChange}
                                optionValueField="selectValue"
                                selectedValue={selectedGroupBySelectValue}
                                noValue={false}
                                isClearable
                                isDisabled={!!disabledReason}
                                placeholder="Select..."
                                showStatusOnSelectedValue={false}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {disabledReason && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            {disabledReason}
                        </Typography>
                    </Grid>
                )}

                {!disabledReason && draft && (
                    <Grid item xs={12}>
                        <Grid container direction="column" rowGap={2}>
                            <Grid item>{renderEditor(draft)}</Grid>

                            {showOrientChartByToggle && (
                                <Grid item>
                                    <AcxToggleButton
                                        id={`${chartStore.id}-orient-chart-by-x-axis-toggle`}
                                        inputLabel="Orient Chart By (X-Axis)"
                                        items={[
                                            {
                                                id: `${chartStore.id}-orient-chart-by-x-axis-chart`,
                                                value: false,
                                                element:
                                                    chartStore.orientChartByXAxisLabel,
                                            },
                                            {
                                                id: `${chartStore.id}-orient-chart-by-x-axis-grouping`,
                                                value: true,
                                                element:
                                                    selectedGroupByMenuOption?.label ??
                                                    fallbackOptionLabel ??
                                                    "Grouping",
                                            },
                                        ]}
                                        exclusive
                                        defaultValue={orientChartByValue}
                                        onChange={(value) => {
                                            if (typeof value !== "boolean") {
                                                return;
                                            }

                                            setLocalVizOptions((previous) => ({
                                                ...previous,
                                                swapGroupingAndX: value,
                                            }));
                                        }}
                                        labelSx={configureInputLabelSx}
                                        style={{
                                            width: "100%",
                                        }}
                                        sx={{
                                            width: "100%",
                                            "& .MuiToggleButton-root": {
                                                flex: 1,
                                                minWidth: 0,
                                            },
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Grid>
        );
    },
);

export default GroupByConfigurationSection;
